import { Injectable } from '@angular/core';
import { BADGE_DEFINITIONS } from '../constants/badge-definitions';

export interface IComputedBadge {
    badgeKey: string;
    tier: number;
    earnedAt: string;
}

export interface IEnrichedBadge extends IComputedBadge {
    label: string;
    description: string;
    icon: string;
    image: string;
    category: string;
    views: string[];
}

@Injectable({
    providedIn: 'root',
})
export class BadgeService {

    constructor() {}

    enrichBadges(computedBadges: IComputedBadge[]): IEnrichedBadge[] {
        if (!computedBadges || computedBadges.length === 0) {
            return [];
        }

        return computedBadges
            .map((badge) => {
                const def = BADGE_DEFINITIONS[badge.badgeKey];
                if (!def || !def.enabled) return null;
                return {
                    ...badge,
                    label: def.label,
                    description: def.description,
                    icon: def.icon,
                    image: def.image,
                    category: def.category,
                    views: def.views,
                };
            })
            .filter(Boolean);
    }

    getBadgesForView(computedBadges: IComputedBadge[]): IEnrichedBadge[] {
        const enriched = this.enrichBadges(computedBadges);
        const filtered = enriched.filter((b) => !this.isStaleBadge(b));

        // Dedup V1/V2 badges with same label — keep highest tier
        const labelMap = new Map<string, IEnrichedBadge>();
        for (const badge of filtered) {
            const existing = labelMap.get(badge.label);
            if (!existing || (badge.tier || 1) > (existing.tier || 1)) {
                labelMap.set(badge.label, badge);
            }
        }
        return Array.from(labelMap.values());
    }

    getBadgeIcon(badgeKey: string): string {
        const def = BADGE_DEFINITIONS[badgeKey];
        return def ? def.icon : 'fa-certificate';
    }

    getBadgeImagePath(badgeKey: string): string | null {
        const def = BADGE_DEFINITIONS[badgeKey];
        return def && def.image ? `assets/images/badges/${def.image}` : null;
    }

    /**
     * Checks if a time-based badge is stale and should be hidden.
     * "Active This Week" is stale if earnedAt is from a different ISO week.
     */
    private isStaleBadge(badge: IEnrichedBadge): boolean {
        if (badge.badgeKey === 'activeThisWeek' && badge.earnedAt) {
            const earnedDate = new Date(badge.earnedAt);
            const now = new Date();
            return this.getISOWeekKey(earnedDate) !== this.getISOWeekKey(now);
        }
        return false;
    }

    private getISOWeekKey(date: Date): string {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
        return `${d.getUTCFullYear()}-W${weekNo < 10 ? '0' : ''}${weekNo}`;
    }
}
